import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { ThemeProvider } from './lib/ThemeContext';
import { LanguageProvider } from './lib/LanguageContext';
import { BuildingProvider } from './lib/BuildingContext';
import { Toaster } from './components/ui/sonner';
import { AddToHomeBanner } from './components/InstallApp';
import Layout from './components/Layout';
import { isViewerRole, homePath } from './lib/roles';

// Lazy-load all pages for code splitting
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const TrainingPage = lazy(() => import('./pages/TrainingPage'));
const ModuleViewerPage = lazy(() => import('./pages/ModuleViewerPage'));
const CertificatePage = lazy(() => import('./pages/CertificatePage'));
const CertificatesListPage = lazy(() => import('./pages/CertificatesListPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const AdminModulesPage = lazy(() => import('./pages/AdminModulesPage'));
const ModuleBuilderPage = lazy(() => import('./pages/ModuleBuilderPage'));
const AdminAssociatesPage = lazy(() => import('./pages/AdminAssociatesPage'));
const AdminAssignmentsPage = lazy(() => import('./pages/AdminAssignmentsPage'));
const AssignedTasksPage = lazy(() => import('./pages/AssignedTasksPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const ShortsPage = lazy(() => import('./pages/ShortsPage'));
const AdminManagersPage = lazy(() => import('./pages/AdminManagersPage'));
const AdminThumbnailsPage = lazy(() => import('./pages/AdminThumbnailsPage'));
const AdminCategoriesPage = lazy(() => import('./pages/AdminCategoriesPage'));
const AdminSkillMatrixPage = lazy(() => import('./pages/AdminSkillMatrixPage'));
const AdminMediaPage = lazy(() => import('./pages/AdminMediaPage'));
const AdminObservationsPage = lazy(() => import('./pages/AdminObservationsPage'));
const TrainingSignOffPage = lazy(() => import('./pages/TrainingSignOffPage'));
const MeetingsPage = lazy(() => import('./pages/MeetingsPage'));
const AdminBuildingsPage = lazy(() => import('./pages/AdminBuildingsPage'));
const PITLicensesPage = lazy(() => import('./pages/PITLicensesPage'));
const KaizensPage = lazy(() => import('./pages/KaizensPage'));
const SOPsPage = lazy(() => import('./pages/SOPsPage'));
const SignPage = lazy(() => import('./pages/SignPage'));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage'));
const PortalDashboardPage = lazy(() => import('./pages/PortalDashboardPage'));
const PortalAssociatesPage = lazy(() => import('./pages/PortalAssociatesPage'));
const PortalProfilePage = lazy(() => import('./pages/PortalProfilePage'));
const AdminPortalAccountsPage = lazy(() => import('./pages/AdminPortalAccountsPage'));

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <div className="animate-spin h-10 w-10 border-4 border-[#003366] border-t-transparent rounded-full" />
    </div>
  );
}

function ProtectedRoute({ children, adminOnly = false, superAdminOnly = false, viewer = false, viewerOnly = false, bare = false }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (isViewerRole(user)) {
    if (!viewer && !viewerOnly) return <Navigate to="/portal" replace />;
    return <Layout>{children}</Layout>;
  }
  if (viewerOnly) return <Navigate to={homePath(user)} replace />;
  if (adminOnly && user.role !== 'admin' && user.role !== 'manager') return <Navigate to="/dashboard" replace />;
  if (superAdminOnly && user.role !== 'admin') return <Navigate to="/admin" replace />;
  if (bare) return children;
  return <Layout>{children}</Layout>;
}

function PasswordChangeRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/" replace />;
  if (!user.must_change_password) return <Navigate to={homePath(user)} replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (user) {
    if (user.must_change_password) return <Navigate to="/change-password" replace />;
    return <Navigate to={homePath(user)} replace />;
  }
  return children;
}

// Registrar "forward with masking" frames load paths like "//sign/abc"; collapse repeated slashes.
function NormalizedPath({ children }) {
  const location = useLocation();
  if (/\/{2,}/.test(location.pathname)) {
    const clean = location.pathname.replace(/\/{2,}/g, '/');
    return <Navigate to={`${clean}${location.search}${location.hash}`} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <NormalizedPath>
      <Routes>
        <Route path="/" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<Navigate to="/" replace />} />
        <Route path="/change-password" element={<PasswordChangeRoute><ChangePasswordPage /></PasswordChangeRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/assigned-tasks" element={<ProtectedRoute><AssignedTasksPage /></ProtectedRoute>} />
        <Route path="/training" element={<ProtectedRoute><TrainingPage /></ProtectedRoute>} />
        <Route path="/module/:id" element={<ProtectedRoute><ModuleViewerPage /></ProtectedRoute>} />
        <Route path="/certificates" element={<ProtectedRoute><CertificatesListPage /></ProtectedRoute>} />
        <Route path="/certificate/:id" element={<ProtectedRoute><CertificatePage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
        <Route path="/shorts" element={<ProtectedRoute bare><ShortsPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboardPage /></ProtectedRoute>} />
        <Route path="/admin/modules" element={<ProtectedRoute superAdminOnly><AdminModulesPage /></ProtectedRoute>} />
        <Route path="/admin/modules/:id/builder" element={<ProtectedRoute superAdminOnly><ModuleBuilderPage /></ProtectedRoute>} />
        <Route path="/admin/assignments" element={<ProtectedRoute adminOnly><AdminAssignmentsPage /></ProtectedRoute>} />
        <Route path="/admin/associates" element={<ProtectedRoute adminOnly><AdminAssociatesPage /></ProtectedRoute>} />
        <Route path="/admin/managers" element={<ProtectedRoute superAdminOnly><AdminManagersPage /></ProtectedRoute>} />
        <Route path="/admin/thumbnails" element={<ProtectedRoute superAdminOnly><AdminThumbnailsPage /></ProtectedRoute>} />
        <Route path="/admin/categories" element={<ProtectedRoute superAdminOnly><AdminCategoriesPage /></ProtectedRoute>} />
        <Route path="/admin/reviews" element={<Navigate to="/admin" replace />} />
        <Route path="/admin/skill-matrix" element={<ProtectedRoute adminOnly viewer><AdminSkillMatrixPage /></ProtectedRoute>} />
        <Route path="/admin/media" element={<ProtectedRoute adminOnly><AdminMediaPage /></ProtectedRoute>} />
        <Route path="/admin/observations" element={<ProtectedRoute adminOnly><AdminObservationsPage /></ProtectedRoute>} />
        <Route path="/training-signoff" element={<ProtectedRoute><TrainingSignOffPage /></ProtectedRoute>} />
        <Route path="/admin/meetings" element={<ProtectedRoute adminOnly><MeetingsPage /></ProtectedRoute>} />
        <Route path="/admin/buildings" element={<ProtectedRoute superAdminOnly><AdminBuildingsPage /></ProtectedRoute>} />
        <Route path="/admin/pit-licenses" element={<ProtectedRoute adminOnly><PITLicensesPage /></ProtectedRoute>} />
        <Route path="/admin/kaizens" element={<ProtectedRoute adminOnly><KaizensPage /></ProtectedRoute>} />
        <Route path="/sops" element={<ProtectedRoute viewer><SOPsPage /></ProtectedRoute>} />
        <Route path="/portal" element={<ProtectedRoute viewerOnly><PortalDashboardPage /></ProtectedRoute>} />
        <Route path="/portal/associates" element={<ProtectedRoute viewerOnly><PortalAssociatesPage /></ProtectedRoute>} />
        <Route path="/portal/profile" element={<ProtectedRoute viewerOnly><PortalProfilePage /></ProtectedRoute>} />
        <Route path="/admin/portal-accounts" element={<ProtectedRoute superAdminOnly><AdminPortalAccountsPage /></ProtectedRoute>} />
        <Route path="/sign/:token" element={<SignPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </NormalizedPath>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <BuildingProvider>
              <AppRoutes />
              <AddToHomeBanner />
              <Toaster position="top-right" richColors />
            </BuildingProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
